/**
 * COVID Penalty Recovery funnel — API client (kgob.com side).
 *
 * Architecture (confirmed with user, Jun 2026):
 *   • kgob.com hosts the PUBLIC surface only: landing page + legal pages (Terms,
 *     Refund & Dispute Policy) + the free 2-minute eligibility check.
 *   • ALL real data collection, login, profile, payment/payout and the paid
 *     funnel (Checkout, Client Portal, Data Collection & Validation, Find My
 *     Case, Retrieve Case Number) live on app.kgob.com.
 *   • Our job: render the design and do a SMART HANDOVER WITH A SESSION — mint a
 *     session token, attach the lightweight free-check signal to it, pass it
 *     through every CTA, and record the handover in the shared CRM bridge so the
 *     SAME visitor can be tracked end-to-end (kgob.com → app.kgob.com) and
 *     app.kgob.com can reconcile the session and continue the journey.
 *
 * See /app/docs/PENALTY_RECOVERY_HANDOFF_FOR_APP_KGOB.md
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "pr_session_id";

/**
 * APPLICATIONS CLOSED (June 2026): the remaining time before the CPA submission
 * deadline is too short to responsibly prepare new claims. New checkout handovers
 * are BLOCKED — blocked CTAs route to the on-page contact form (#pr-closed) that
 * collects name/email/phone instead. Existing clients (login/portal/find-case/
 * retrieve-case) are unaffected. Flip to false to reopen the funnel.
 */
export const APPLICATIONS_CLOSED = true;
const CLOSED_BLOCKED_TARGETS = new Set(["review"]);

/** Mint (once) and persist a session token for this visitor's penalty-recovery journey. */
export function getSessionId() {
  if (typeof window === "undefined") return null;
  try {
    let id = window.localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `prs_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      window.localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch (e) {
    return `prs_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Reconcile the session token app.kgob.com round-trips back to us (e.g. on the
 * thank-you page: kgob.com/penalty-recovery/thank-you?session=...). If a session
 * param is present we adopt it as THIS visitor's session so every subsequent CTA
 * (Sign in, etc.) reuses the same id and app.kgob.com can stitch the journey.
 * Returns the effective session id.
 */
export function ingestSessionFromUrl() {
  if (typeof window === "undefined") return getSessionId();
  try {
    const param = new URLSearchParams(window.location.search).get("session");
    if (param && param.length >= 6 && param.length <= 128) {
      window.localStorage.setItem(SESSION_KEY, param);
      return param;
    }
  } catch (e) {
    /* fall through to mint/return existing */
  }
  return getSessionId();
}

/**
 * Resolve app.kgob.com base. Production hostname ALWAYS routes to production
 * (a stale preview env var can never send real users to a preview funnel);
 * preview/localhost honor REACT_APP_APP_KGOB_BASE_URL, else fail-safe to prod.
 */
function resolveAppKgobBase() {
  if (typeof window !== "undefined") {
    const host = (window.location && window.location.hostname) || "";
    if (host === "kgob.com" || host === "www.kgob.com") {
      return "https://app.kgob.com";
    }
  }
  return process.env.REACT_APP_APP_KGOB_BASE_URL || "https://app.kgob.com";
}

// Outbound funnel destinations on app.kgob.com. Paths documented in the handoff
// spec; app.kgob.com confirms/owns the final routes. Every URL carries `session`
// so app.kgob.com can reconcile this visitor with their kgob.com handover record.
const CTA_PATHS = {
  review: "/penalty-recovery/checkout",      // "Start My Review — $295"
  login: "/penalty-recovery/login",          // "Sign in" (returning clients)
  portal: "/penalty-recovery/portal",
  data: "/penalty-recovery/intake",          // Data Collection & Validation
  find: "/penalty-recovery/find-case",
  retrieve: "/penalty-recovery/retrieve-case",
};

export function buildCtaUrl(target, source) {
  const path = CTA_PATHS[target] || CTA_PATHS.review;
  const p = new URLSearchParams();
  p.set("session", getSessionId() || "");
  p.set("utm_source", `kgob_${source || target}`);
  p.set("utm_medium", "internal");
  p.set("utm_campaign", "covid_penalty_recovery");
  return `${resolveAppKgobBase()}${path}?${p.toString()}`;
}

/** Fire-and-forget handover/CTA breadcrumb to OUR backend → bridge. Never blocks navigation. */
export function trackCTA(target, source) {
  try {
    const body = JSON.stringify({
      session_id: getSessionId(),
      target: target || null,
      source: source || null,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
      path: typeof window !== "undefined" ? window.location.pathname : null,
    });
    const url = `${BACKEND}/api/v1/penalty-recovery/track-cta`;
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    } else {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch (e) {
    /* never block on tracking */
  }
}

/** Scroll to the applications-closed contact form; navigate to the landing if it isn't on this page. */
export function goToClosedForm() {
  if (typeof window === "undefined") return;
  const el = document.getElementById("pr-closed");
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  } else {
    window.location.href = "/penalty-recovery#pr-closed";
  }
}

/** Navigate to an app.kgob.com funnel destination, recording the handover first. */
export function openCta(target, source) {
  if (APPLICATIONS_CLOSED && CLOSED_BLOCKED_TARGETS.has(target)) {
    trackCTA(`closed_blocked_${target}`, source);
    goToClosedForm();
    return;
  }
  trackCTA(target, source);
  if (typeof window !== "undefined") {
    window.location.href = buildCtaUrl(target, source);
  }
}

/**
 * Applications-closed contact capture: name/email/phone of a visitor who still
 * wants to talk to a CPA. Stored on the session record and synced to the bridge.
 */
export async function submitWaitlistLead(payload) {
  const res = await fetch(`${BACKEND}/api/v1/penalty-recovery/waitlist`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: getSessionId(), ...payload }),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail.detail || "Could not save your details. Please try again.");
  }
  return res.json();
}

/**
 * Attach the free 2-minute check signal (answers + eligibility + optional email)
 * to this visitor's session handover record in the shared bridge. app.kgob.com
 * reads the session to send the summary + deadline-reminder emails and to
 * continue the journey. Returns the parsed JSON or throws.
 */
export async function submitEligibilityLead(payload) {
  const res = await fetch(`${BACKEND}/api/v1/penalty-recovery/eligibility-lead`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: getSessionId(), ...payload }),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail.detail || "Could not save your details. Please try again.");
  }
  return res.json();
}


/**
 * Fallback lookup: resolve this session's KGOB case number via our backend proxy
 * (which calls app.kgob.com server-side with the secret key). Used on the thank-you
 * page when the ?case= URL param is missing. Returns { found, case_number } or { found:false }.
 */
export async function fetchCaseBySession(sessionId) {
  if (!sessionId) return { found: false };
  try {
    const res = await fetch(`${BACKEND}/api/v1/penalty-recovery/case-by-session?session=${encodeURIComponent(sessionId)}`);
    if (!res.ok) return { found: false };
    return await res.json();
  } catch (e) {
    return { found: false };
  }
}
